import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import set from "lodash.set";
import moment from "moment";
import { withSnackbar } from "notistack";
import React, { useState, useEffect } from "react";

import AlertDialog from "../Alert";
import LoadingButton from "../../Buttons/Loading";
import Divider from "../../Dividers/Dialog";
import MediaImage from "../../MediaImage";
import Header from "../../Typography/Header";
import { moment as momentConst } from "../../../constants";
import store from "../../../store";
import {
  updatePatrol,
  getPatrol,
  deletePatrolMedia,
} from "../../../utils/appsyncOperations";
import { getEditableValue } from "../../../utils/input";

const darkTheme = createMuiTheme({
  palette: {
    type: "dark",
  },
});

const styles = {
  pic: {
    height: "100%",
    width: "100%",
    objectFit: "contain",
    overFlow: "hidden",
  },
  loading: {
    display: "flex",
    flex: 1,
    margin: "16px 0px 32px 16px",
    justifyContent: "center",
    alignItems: "center",
  },
  menus: {
    width: 200,
  },
  picGrid: {
    height: 300,
  },
  picDesc: {
    marginBottom: 24,
  },
};

const useStyles = makeStyles({
  circularProgressContainer: {
    alignItems: "center",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  imageContainer: {
    minHeight: 80,
    position: "relative",
  },
  imageToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    left: 8,
    position: "absolute",
    right: 8,
    top: 11,
  },
  imageToolbarButton: {
    margin: 8,
  },
  imageToolbarButtonContainer: {
    position: "relative",
  },
  imageToolbarButtonsContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.38)",
    borderBottomLeftRadius: 8,
  },
});

const PropertyDialog = ({
  isOpen,
  onClose,
  enqueueSnackbar,
  reportId = null,
  onSaveSuccess = () => {},
}) => {
  const classes = useStyles();
  const [activityReport, setActivityReport] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteAlert, setDeleteAlert] = useState({
    onConfirm: () => null,
    open: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setActivityReport(null);
      setIsEditable(false);
      setSaveLoading(false);
      setLoading(true);
      init();
    }
  }, [isOpen]);

  const closeDeleteAlert = () =>
    setDeleteAlert((prevState) => ({ ...prevState, open: false }));

  const init = async () => {
    try {
      const report = await getPatrol(reportId);
      setActivityReport(report);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e, { variant: "error" });
    }
    setLoading(false);
  };

  const onSave = async () => {
    setSaveLoading(true);
    try {
      const input = {
        patrolId: reportId,
        notes: activityReport.notes,
        attachedMedia: activityReport.attachedMedia,
      };
      const response = await updatePatrol(input);
      onSaveSuccess(response);
      onClose();
      enqueueSnackbar("Activity report updated!", { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e, { variant: "error" });
      setSaveLoading(false);
    }
  };

  const setValue = (index, path, value, type) => {
    const temp = [...activityReport[type]];
    set(temp[index], path, value);
    setActivityReport({
      ...activityReport,
      [type]: temp,
    });
  };

  const setMediaValue = (index, path, value) => {
    setValue(index, path, value, "attachedMedia");
  };

  const mutationPending = saveLoading || deleteLoading;

  const renderMedia = () => {
    return (
      Array.isArray(activityReport.attachedMedia) &&
      activityReport.attachedMedia.length > 0 && (
        <>
          <Divider />
          <Header label="Media" />
          {activityReport.attachedMedia ? (
            activityReport.attachedMedia.map((media, index) => {
              return (
                <Grid
                  container
                  justify="space-between"
                  key={media.mediaId}
                  spacing={2}
                >
                  <Grid className={classes.imageContainer} item xs={12}>
                    <MediaImage media={media} style={styles.pic} />
                    <div className={classes.imageToolbar}>
                      <div className={classes.imageToolbarButtonsContainer}>
                        <div className={classes.imageToolbarButtonContainer}>
                          <ThemeProvider theme={darkTheme}>
                            <IconButton
                              className={classes.imageToolbarButton}
                              disabled={isEditable || mutationPending}
                              onClick={() => {
                                setDeleteIndex(index);
                                setDeleteAlert({
                                  onConfirm: async () => {
                                    setDeleteLoading(true);
                                    try {
                                      const updatedPatrol = await deletePatrolMedia(
                                        { mediaId: media.mediaId, reportId }
                                      );
                                      setActivityReport(updatedPatrol);
                                      onSaveSuccess(updatedPatrol);
                                      enqueueSnackbar("Media deleted!", {
                                        variant: "success",
                                      });
                                    } catch (error) {
                                      enqueueSnackbar(error, {
                                        variant: "error",
                                      });
                                    }
                                    setDeleteLoading(false);
                                  },
                                  open: true,
                                });
                              }}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ThemeProvider>
                          {deleteLoading && index === deleteIndex && (
                            <div className={classes.circularProgressContainer}>
                              <CircularProgress size={24} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Grid>
                  {Boolean(media.createdAt) && (
                    <Grid item xs={12}>
                      <TextField
                        label="Created At"
                        value={moment(media.createdAt)
                          .tz(timezone)
                          .format(momentConst.format)}
                        disabled={isEditable}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      style={styles.picDesc}
                      label="Description"
                      value={getEditableValue(media.description, isEditable)}
                      onChange={(e) =>
                        setMediaValue(index, "description", e.target.value)
                      }
                      InputProps={{ readOnly: !isEditable }}
                      variant="outlined"
                      fullWidth
                      disabled={isEditable && mutationPending}
                    />
                  </Grid>
                </Grid>
              );
            })
          ) : (
            <Typography variant="body1">No media added.</Typography>
          )}
        </>
      )
    );
  };

  let timezone;
  if (activityReport) {
    const property = store.getState().properties.byId[
      activityReport.propertyId
    ];
    if (property) {
      timezone = property.place.timezone;
    }
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          Daily Activity Report{`${isEditable ? " \u2022 Editable" : ""}`}
        </DialogTitle>
        {loading ? (
          <div style={styles.loading}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <DialogContent>
              <Header label="Summary" />
              <Grid container spacing={2} justify="space-between">
                <Grid item xs={6} s={6}>
                  <TextField
                    label="Started At"
                    value={moment(activityReport.startTime)
                      .tz(timezone)
                      .format(momentConst.format)}
                    InputProps={{ readOnly: true }}
                    disabled={isEditable}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} s={6}>
                  <TextField
                    label="Ended At"
                    value={moment(activityReport.endTime)
                      .tz(timezone)
                      .format(momentConst.format)}
                    InputProps={{ readOnly: true }}
                    disabled={isEditable}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Created By"
                    value={activityReport.displayName}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled={isEditable}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Report ID"
                    value={activityReport.patrolId}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled={isEditable}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    label="Notes"
                    value={getEditableValue(activityReport.notes, isEditable)}
                    onChange={(e) =>
                      setActivityReport({
                        ...activityReport,
                        notes: e.target.value,
                      })
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        InputProps={{
                          readOnly: true,
                        }}
                        checked={
                          (activityReport.isVacationWatch ||
                            activityReport.isCardinal) === true
                        }
                        disabled={isEditable}
                        color="primary"
                      />
                    }
                    label="During VIP Status"
                  />
                </Grid>
              </Grid>
              {renderMedia()}
            </DialogContent>
            <DialogActions>
              {isEditable ? (
                <LoadingButton
                  isLoading={saveLoading}
                  onClick={onSave}
                  color="primary"
                >
                  Save
                </LoadingButton>
              ) : (
                <Button
                  onClick={() => setIsEditable(true)}
                  color="primary"
                  disabled={deleteLoading}
                >
                  Edit
                </Button>
              )}

              <Button onClick={onClose} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <AlertDialog
        content="Are you sure you want to delete this image?"
        isOpen={deleteAlert.open}
        onClose={closeDeleteAlert}
        onNo={closeDeleteAlert}
        onYes={() => {
          closeDeleteAlert();
          deleteAlert.onConfirm();
        }}
      />
    </>
  );
};

export default withSnackbar(PropertyDialog);
